import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Users, Briefcase, Star, UserPlus, ExternalLink, Mail, Phone, AlertCircle, Calendar, Trash2, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TeamRow } from './TeamRow';
import { ScreenshotThumb } from '../competitors/ScreenshotThumb';
import type { SportWithTeams, Gender, Season, CompetitorFundraiser, CompetitorPlatform, BlastFundraiser, LinkedPerson, MunchFundCampaign } from '../../types';
import { SourceIndicator } from './SourceIndicator';
import { BlastBadge } from './BlastBadge';
import { CrmOnlyBadge } from './CrmOnlyBadge';
import { effectiveFirstName, effectiveLastName, effectiveEmail, effectivePhone } from '../../lib/personPrecedence';
import { sportSeasonOverrideApi } from '../../lib/api';
import { useOrganizationStore } from '../../store/organizationStore';
import toast from 'react-hot-toast';
import { formatSportLabel } from '../../lib/sportLabel';
import { isHiddenTeam } from '../../lib/teamVisibility';

interface SportCardProps {
  sport: SportWithTeams;
  organizationId: string;
  onAddTeam: (sportId: string, sportDisplayName: string) => void;
  onLinkPerson?: (sportId: string, teamId: string | null) => void;
  onAddFundraiser?: (sportId: string) => void;
  /** Opens the edit window for one of this sport's competitor fundraisers. */
  onEditFundraiser?: (fundraiser: CompetitorFundraiser) => void;
  fundraisers?: CompetitorFundraiser[];
  blastFundraisers?: BlastFundraiser[];
  munchFundCampaigns?: MunchFundCampaign[];
  defaultExpanded?: boolean;
  onScheduleActivity?: (personId: string, personName: string) => void;
  onConvert?: (personId: string, teamId: string, sportId: string) => void;
  /**
   * Gates the Add Team / Add Person / Add Fundraiser / season-override Reset
   * affordances. Defaults to true to preserve existing callsites.
   */
  canEdit?: boolean;
  /** ADMIN-only: enables the delete control on this (CRM-only) sport. */
  canDelete?: boolean;
  /** Opens the delete confirm for this sport. Only provided to admins. */
  onRequestDeleteSport?: (sport: SportWithTeams) => void;
  onRequestDeleteTeam?: (team: SportWithTeams['teams'][number], sportId: string) => void;
  onRequestUnlinkCoach?: (link: LinkedPerson) => void;
  /** When true, empty teams (no roster, no current people) join the main list. */
  showEmptyTeams?: boolean;
}

const seasonLabel = (season: Season): string => {
  switch (season) {
    case 'FALL': return 'Fall';
    case 'WINTER': return 'Winter';
    case 'SPRING': return 'Spring';
    case 'SUMMER': return 'Summer';
  }
};

const seasonColor = (season: Season): string => {
  switch (season) {
    case 'FALL': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    case 'WINTER': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'SPRING': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'SUMMER': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
  }
};

function formatCompactCurrency(amount: number): string {
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${amount}`;
}

function currentSyBounds(now: Date = new Date()): { start: Date; end: Date } {
  const m = now.getUTCMonth();
  const year = m >= 6 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
  return {
    start: new Date(Date.UTC(year, 6, 1)),
    end: new Date(Date.UTC(year + 1, 6, 1)),
  };
}

function inCurrentSy(launchedAt: string | null, bounds: { start: Date; end: Date }): boolean {
  if (!launchedAt) return false;
  const t = new Date(launchedAt);
  return t >= bounds.start && t < bounds.end;
}

const platformBadgeColor = (platform: CompetitorPlatform): string => {
  switch (platform) {
    case 'VERTICAL_RAISE': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    case 'SNAP_RAISE': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    case 'ETEAM_SPONSOR': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'SCHOOL_FUNDR': return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
};

const platformLabel = (platform: CompetitorPlatform): string => {
  switch (platform) {
    case 'VERTICAL_RAISE': return 'VR';
    case 'SNAP_RAISE': return 'Snap';
    case 'ETEAM_SPONSOR': return 'eTeam';
    case 'SCHOOL_FUNDR': return 'SF';
    default: return platform;
  }
};

function CompactFundraiserRow({
  fundraiser,
  onEdit,
}: {
  fundraiser: CompetitorFundraiser;
  onEdit?: (fundraiser: CompetitorFundraiser) => void;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${platformBadgeColor(fundraiser.platform)}`}>
          {platformLabel(fundraiser.platform)}
        </span>
        <span className="text-gray-700 dark:text-gray-300 truncate">
          {fundraiser.campaignName || `${fundraiser.year || ''} Fundraiser`}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <ScreenshotThumb fundraiser={fundraiser} />
        {fundraiser.raisedAmount != null && (
          <span className="text-amber-600 dark:text-amber-400 font-medium">
            {formatCompactCurrency(Number(fundraiser.raisedAmount))}
          </span>
        )}
        {fundraiser.campaignUrl && (
          <a
            href={fundraiser.campaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {onEdit && (
          <button
            type="button"
            onClick={() => onEdit(fundraiser)}
            aria-label="Edit fundraiser"
            title="Edit fundraiser"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export const SportCard = ({
  sport,
  organizationId,
  onAddTeam,
  onLinkPerson,
  onAddFundraiser,
  onEditFundraiser,
  fundraisers,
  blastFundraisers = [],
  munchFundCampaigns = [],
  defaultExpanded = false,
  onScheduleActivity,
  onConvert,
  canEdit = true,
  canDelete = false,
  onRequestDeleteSport,
  onRequestDeleteTeam,
  onRequestUnlinkCoach,
  showEmptyTeams = false,
}: SportCardProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showFormerTeams, setShowFormerTeams] = useState(false);
  // Per-sport + per-team meta lines show current-SY only (lifetime is in the rollup banner).
  const syBounds = currentSyBounds();
  const currentSyBlastFundraisers = blastFundraisers.filter((f) => inCurrentSy(f.launchedAt, syBounds));

  // Auto-expand when the URL hash targets this sport (e.g. from a BlastFundraisers
  // row anchor). Listen for hashchange so subsequent clicks re-trigger expansion
  // even when the hash already matches.
  useEffect(() => {
    const matchesHash = () => window.location.hash === `#sport-${sport.id}`;
    if (matchesHash()) setIsExpanded(true);
    const onHashChange = () => { if (matchesHash()) setIsExpanded(true); };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [sport.id]);

  const displayName = formatSportLabel(sport.gender, sport.name);
  const allTeams = sport.teams || [];
  // Two independent axes (see teamVisibility.ts): ROSTER (empty = no roster + no
  // current people) and RECENCY (isActive). Three display buckets:
  //   current = active & has content   → main list (default)
  //   former  = inactive & has content → "former teams" reveal
  //   empty   = no roster, no current people → "Show empty teams"
  const currentTeams = allTeams.filter((t) => t.isActive && !isHiddenTeam(t));
  const formerTeams = allTeams.filter((t) => !t.isActive && !isHiddenTeam(t));
  const emptyTeams = allTeams.filter((t) => isHiddenTeam(t));
  // When "Show empty teams" is on, empty teams (current OR former) join the main list.
  const mainTeams = showEmptyTeams ? [...currentTeams, ...emptyTeams] : currentTeams;
  // Counts are CURRENT-only (endDate null) — the payload includes former links.
  const isCurrentLink = (link: { endDate: string | null }) => link.endDate == null;
  const teamPeopleCount = currentTeams.reduce((sum, t) => sum + (t.people?.filter(isCurrentLink).length || 0), 0);
  const totalPeople = (sport.people?.filter(isCurrentLink).length || 0) + teamPeopleCount;
  // "In Blast" is about ORIGIN, not status — any team that came from Blast counts,
  // active/empty/former alike. "CRM only" is reserved for genuinely rep-created teams.
  const hasBlastTeams = allTeams.some((t) => t.blastTeamId);

  // Admin delete gate: only CRM-only sports, blocked until no active teams / current sport-level coaches.
  const sportHasActiveTeam = allTeams.some((t) => t.isActive);
  const sportHasCurrentPeople = (sport.people?.some((l) => l.endDate == null)) ?? false;
  const canShowSportDelete = canDelete && !hasBlastTeams && !!onRequestDeleteSport;
  const sportDeleteBlocked = sportHasActiveTeam || sportHasCurrentPeople;

  const headerBgClasses = hasBlastTeams
    ? 'bg-green-50/40 hover:bg-green-50/70 dark:bg-green-900/10 dark:hover:bg-green-900/20'
    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50';

  return (
    <div id={`sport-${sport.id}`} className="card overflow-hidden scroll-mt-4">
      {/* Sport header — mobile-first stacking. */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full text-left transition-colors px-5 py-4 ${headerBgClasses}`}
      >
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          {/* Title + pills (wraps as needed on small viewports) */}
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
            )}
            <span className="text-base font-semibold text-gray-900 dark:text-gray-100 min-w-0 break-words">
              {displayName}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${seasonColor(sport.season)}`}>
              {seasonLabel(sport.season)}
            </span>
            {sport.isSeasonOverridden && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                title="Season was set by a rep, overriding the state default"
              >
                Overridden
              </span>
            )}
            {sport.isSeasonOverridden && canEdit && (
              <button
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    await sportSeasonOverrideApi.reset(organizationId, sport.id);
                    await useOrganizationStore.getState().fetchOrganization(organizationId);
                    toast.success('Season reset to state default');
                  } catch (err) {
                    const message = err instanceof Error ? err.message : 'Failed to reset season';
                    toast.error(message);
                  }
                }}
                className="text-xs text-blast-600 hover:underline dark:text-blast-400"
              >
                Reset to state default
              </button>
            )}
            {hasBlastTeams ? (
              <BlastBadge label="In Blast" />
            ) : (
              <CrmOnlyBadge title="No team in this sport is in Blast yet." />
            )}
            {canShowSportDelete && (
              <button
                type="button"
                aria-label={`Delete sport ${displayName}`}
                title={sportDeleteBlocked ? "Remove this sport's teams and coaches first" : 'Delete this sport'}
                disabled={sportDeleteBlocked}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRequestDeleteSport!(sport);
                }}
                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-40 disabled:hover:text-gray-400 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Counts + Blast SY metric — wraps on mobile, stays compact on desktop. */}
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 flex-wrap pl-7 md:pl-0 md:flex-shrink-0">
            <span>{currentTeams.length} {currentTeams.length === 1 ? 'team' : 'teams'}</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span>{totalPeople} {totalPeople === 1 ? 'person' : 'people'}</span>
            {currentSyBlastFundraisers.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span className="text-green-600 dark:text-green-400" title="Current school year (Jul 1 → Jun 30)">
                  Blast this SY: {currentSyBlastFundraisers.length} ·{' '}
                  {formatCompactCurrency(
                    currentSyBlastFundraisers.reduce(
                      (sum, f) => sum + (f.totalRaisedCents ? Number(BigInt(f.totalRaisedCents)) / 100 : 0),
                      0,
                    ),
                  )}
                </span>
              </>
            )}
            {fundraisers && fundraisers.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span className="text-amber-600 dark:text-amber-400">
                  {fundraisers.length} fundraiser{fundraisers.length !== 1 ? 's' : ''} · {formatCompactCurrency(fundraisers.reduce((sum, f) => sum + Number(f.raisedAmount || 0), 0))}
                </span>
              </>
            )}
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 space-y-4">
          {/* Sport-level people (no team) */}
          {(sport.people?.length || 0) > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Program Staff
              </h4>
              <div className="space-y-1">
                {(sport.people || []).map((link) => {
                  const firstName = effectiveFirstName(link.person);
                  const lastName = effectiveLastName(link.person);
                  const email = effectiveEmail(link.person);
                  const phone = effectivePhone(link.person);
                  return (
                  <Link
                    key={link.id}
                    to={`/people/${link.person.id}`}
                    className="flex items-start px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                  >
                    <div className="flex-shrink-0 h-7 w-7 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {firstName[0]}{lastName[0]}
                      </span>
                    </div>
                    <div className="ml-2.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <SourceIndicator source={link.person.blastUserId ? 'BLAST' : 'CRM'} />
                        <span className="text-sm text-blast-600 dark:text-blast-400 hover:underline">
                          {firstName} {lastName}
                        </span>
                        {link.role && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <Briefcase className="h-3 w-3 mr-0.5" />
                            {link.role}
                          </span>
                        )}
                        {link.isPrimary && (
                          <Star className="h-3 w-3 text-amber-500 fill-current" />
                        )}
                        {canEdit && onScheduleActivity && (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onScheduleActivity(link.person.id, `${firstName} ${lastName}`); }}
                            className="p-0.5 text-gray-400 hover:text-blast-600 dark:hover:text-blast-400 transition-colors flex-shrink-0"
                            title="Schedule activity"
                          >
                            <Calendar className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDelete && onRequestUnlinkCoach && !link.managedBySync && link.endDate == null && (
                          <button
                            type="button"
                            aria-label={`Unlink ${firstName} ${lastName}`}
                            title="Unlink from this program"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRequestUnlinkCoach(link); }}
                            className="p-0.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs">
                        {email ? (
                          <span className="text-gray-500 dark:text-gray-400 flex items-center truncate">
                            <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                            {email}
                          </span>
                        ) : (
                          <span className="text-red-400 dark:text-red-500 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-0.5" />
                            No email
                          </span>
                        )}
                        {phone ? (
                          <span className="text-gray-500 dark:text-gray-400 flex items-center flex-shrink-0">
                            <Phone className="h-3 w-3 mr-1" />
                            {phone}
                          </span>
                        ) : (
                          <span className="text-red-400 dark:text-red-500 flex items-center flex-shrink-0">
                            <AlertCircle className="h-3 w-3 mr-0.5" />
                            No phone
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Teams (current + empty when "Show empty teams" is on) */}
          {mainTeams.length > 0 ? (
            <div className="space-y-3">
              {mainTeams.map((team) => (
                <TeamRow
                  key={team.id}
                  team={team}
                  blastFundraisers={currentSyBlastFundraisers.filter((f) => f.teamId === team.id)}
                  munchFundCampaigns={munchFundCampaigns.filter((c) => c.teamId === team.id)}
                  onLinkPerson={canEdit && onLinkPerson ? () => onLinkPerson(sport.id, team.id) : undefined}
                  onScheduleActivity={canEdit ? onScheduleActivity : undefined}
                  onConvert={canEdit && onConvert ? (personId, teamId) => onConvert(personId, teamId, sport.id) : undefined}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onRequestDeleteTeam={canDelete && onRequestDeleteTeam ? () => onRequestDeleteTeam(team, sport.id) : undefined}
                  onRequestUnlinkCoach={canDelete ? onRequestUnlinkCoach : undefined}
                />
              ))}
            </div>
          ) : formerTeams.length === 0 ? (
            <div className="text-center py-4">
              <Users className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto" />
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">No teams yet</p>
            </div>
          ) : null}

          {/* Former teams — revealed on demand (CRM-33), client-side only */}
          {formerTeams.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowFormerTeams((v) => !v)}
                aria-expanded={showFormerTeams}
                className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {showFormerTeams ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {showFormerTeams ? 'Hide' : 'Show'} {formerTeams.length} former {formerTeams.length === 1 ? 'team' : 'teams'}
              </button>
              {showFormerTeams && (
                <div className="space-y-3 mt-3 opacity-75">
                  {formerTeams.map((team) => (
                    <TeamRow
                      key={team.id}
                      team={team}
                      blastFundraisers={currentSyBlastFundraisers.filter((f) => f.teamId === team.id)}
                  munchFundCampaigns={munchFundCampaigns.filter((c) => c.teamId === team.id)}
                      onLinkPerson={canEdit && onLinkPerson ? () => onLinkPerson(sport.id, team.id) : undefined}
                      onScheduleActivity={canEdit ? onScheduleActivity : undefined}
                      onConvert={canEdit && onConvert ? (personId, teamId) => onConvert(personId, teamId, sport.id) : undefined}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onRequestDeleteTeam={canDelete && onRequestDeleteTeam ? () => onRequestDeleteTeam(team, sport.id) : undefined}
                      onRequestUnlinkCoach={canDelete ? onRequestUnlinkCoach : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Competitor Fundraisers */}
          {fundraisers && fundraisers.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Competitor Fundraisers
                </span>
                <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full">
                  {fundraisers.length}
                </span>
              </div>
              {fundraisers.map(f => (
                <CompactFundraiserRow key={f.id} fundraiser={f} onEdit={canEdit ? onEditFundraiser : undefined} />
              ))}
            </div>
          )}

          {/* Action buttons — entire row hidden in read-only mode. */}
          {canEdit && (
            <div className="flex gap-2">
              <button
                onClick={() => onAddTeam(sport.id, displayName)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm text-blast-600 dark:text-blast-400 hover:bg-blast-50 dark:hover:bg-blast-900/10 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Team
              </button>
              {onLinkPerson && (
                <button
                  onClick={() => onLinkPerson(sport.id, null)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm text-blast-600 dark:text-blast-400 hover:bg-blast-50 dark:hover:bg-blast-900/10 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 transition-colors"
                >
                  <UserPlus className="h-4 w-4" />
                  Add Person
                </button>
              )}
              {onAddFundraiser && (
                <button
                  onClick={() => onAddFundraiser(sport.id)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-amber-600 dark:text-amber-400 border border-dashed border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Competitor
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
